import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { Button } from 'reactstrap';

import Loading from 'src/components/Loading';
import CancelNominatedTradeMutation from 'src/mutations/CancelNominatedTradeMutation';
import CloseNominatedTradeMutation from 'src/mutations/CloseNominatedTradeMutation';
import FlashesStore from 'src/stores/FlashesStore';
import {
  TRADE_RULE_STATE_ACCEPTED, TRADE_RULE_STATE_CANCELLED, TRADE_RULE_STATE_CLOSED,
  TRADE_RULE_STATE_PROPOSED, TRADE_RULE_STATE_REJECTED,
  TRADE_TYPE_CONTRACTED, TRADE_TYPE_NOMINATED, TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';

import TradeRuleTimelineStepAccepted from './TradeRuleTimelineStepAccepted';
import TradeRuleTimelineStepCancelled from './TradeRuleTimelineStepCancelled';
import TradeRuleTimelineStepClosed from './TradeRuleTimelineStepClosed';
import TradeRuleTimelineStepProposed from './TradeRuleTimelineStepProposed';
import TradeRuleTimelineStepRejected from './TradeRuleTimelineStepRejected';
import { getTradeRuleState, tradeRuleActions } from '../helpers';

const TradeRuleTimelinePropertyFragment = graphql`
  fragment TradeRuleTimeline_property on Property @throwOnFieldError {
    uuid
    title
    timezone
    address {
      line1
      line2
      city
      state
      postcode
      gps {
        latitude
        longitude
      }
    }
    meters {
      edges {
        node {
          uuid
          identifier
          title
          tradePointId
        }
      }
    }
  }
`;

const TradeRuleTimelineRuleFragment = graphql`
  fragment TradeRuleTimeline_rule on TradeRule @throwOnFieldError {
    id
    uuid
    tradeType
    buyer {
      userId
      user { uuid email givenName familyName }
      community { uuid title }
      residualId
      tradePoint {
        uuid
        type
        meter {
          uuid
          identifier
          title
          property {
            uuid
            title
          }
        }
      }
    }
    seller {
      userId
      user { uuid email givenName familyName }
      community { uuid title }
      residualId
      tradePoint {
        uuid
        type
        meter {
          uuid
          identifier
          title
          property {
            uuid
            title
          }
        }
      }
    }
    clauses {
      edges {
        node {
          uuid
          monthsOfYear
          daysOfWeek
          timesOfDay {
            start  { hours minutes seconds }
            finish { hours minutes seconds }
          }
          ignoreDaylightSavings
          ignorePublicHolidays
          price
          timezone
        }
      }
    }
    proposedAt
    proposedBy { uuid email givenName familyName }
    acceptedAt
    acceptedBy { uuid email givenName familyName }
    rejectedAt
    rejectedBy { uuid email givenName familyName }
    cancelledAt
    cancelledBy { uuid email givenName familyName }
    closedAt
    closedBy { uuid email givenName familyName }
  }
`;

/**
 * TradeRuleTimeline functional component displays the timeline of a trade rule
 * and any available actions for the current user.
 * @param {object} props - Component props.
 * @param {object} props.property - Property fragment reference.
 * @param {object} props.rule - TradeRule fragment reference.
 * @returns {React.ReactElement} TradeRuleTimeline component.
 */
function TradeRuleTimeline({ property = null, rule = null }) {
  const intl = useIntl();
  const [processing, setProcessing] = useState(false);

  const propertyData = useFragment(TradeRuleTimelinePropertyFragment, property);
  const ruleData = useFragment(TradeRuleTimelineRuleFragment, rule);

  if (!propertyData || !ruleData) {
    return <Loading />;
  }

  const handleNominatedTradeMutationFailure = (error) => {
    setProcessing(false);
    FlashesStore.flash(FlashesStore.ERROR, error);
  };

  const handleCancelNominatedTradeMutationSuccess = (response) => {
    setProcessing(false);
    const { uuid } = response.cancelNominatedTrade;

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      intl.formatMessage({ id: 'trade_rule.trade_rule_timeline.trade_rule_cancelled.response_message.success', defaultMessage: 'Cancelled peer-to-peer trade rule proposal with id: {uuid}.' }, { uuid }),
    );
  };

  const handleCloseNominatedTradeMutationSuccess = (response) => {
    setProcessing(false);
    const { uuid } = response.closeNominatedTrade;

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      intl.formatMessage({ id: 'trade_rule.trade_rule_timeline.trade_rule_closed.response_message.success', defaultMessage: 'Closed peer-to-peer trade rule with id: {uuid}.' }, { uuid }),
    );
  };

  /**
   * Cancels the given proposed nominated trade rule.
   * @param {object} tradeRule - The trade rule to cancel.
   */
  const cancelTradeRule = (tradeRule) => {
    const { id, uuid } = tradeRule;
    setProcessing(true);
    CancelNominatedTradeMutation(
      { id, uuid },
      handleCancelNominatedTradeMutationSuccess,
      handleNominatedTradeMutationFailure,
    );
  };

  /**
   * Closes the given accepted nominated trade rule.
   * @param {object} tradeRule - The trade rule to close.
   */
  const closeTradeRule = (tradeRule) => {
    const { id, uuid } = tradeRule;
    setProcessing(true);
    CloseNominatedTradeMutation(
      { id, uuid },
      handleCloseNominatedTradeMutationSuccess,
      handleNominatedTradeMutationFailure,
    );
  };

  /**
   * Generates the appropriate trade rule action buttons.
   * @param {object} tradeRule - The trade rule object containing details about the trade rule.
   * @param {object} metersMap - A map of trade point IDs to meter objects.
   * @param {string} tradeRuleState - The current state of the trade rule.
   * @returns {React.ReactComponentElement} - Buttons to render
   */
  const tradeRuleActionButton = (tradeRule, metersMap, tradeRuleState) => {
    const {
      tradeType, buyer, seller, proposedBy,
    } = tradeRule || {};
    const trader = (buyer?.tradePoint?.uuid in metersMap) ? buyer : seller;
    let buttons;
    if (tradeType === TRADE_TYPE_NOMINATED) {
      if (tradeRuleState === TRADE_RULE_STATE_PROPOSED) {
        if (proposedBy?.uuid === trader?.user?.uuid) {
          buttons = (
            <Button color="primary" onClick={() => cancelTradeRule(tradeRule)} disabled={processing}>
              {intl.formatMessage({ id: 'trade_rule.trade_rule_timeline.cancel_trade.label', defaultMessage: 'Cancel trade rule' })}
            </Button>
          );
        }
      } else if (tradeRuleState === TRADE_RULE_STATE_ACCEPTED) {
        buttons = (
          <Button color="primary" onClick={() => closeTradeRule(tradeRule)} disabled={processing}>
            {intl.formatMessage({ id: 'trade_rule.trade_rule_timeline.close_trade.label', defaultMessage: 'Close trade rule' })}
          </Button>
        );
      }
    } else if (tradeType === TRADE_TYPE_CONTRACTED || tradeType === TRADE_TYPE_RESIDUAL) {
      if (tradeRuleState === TRADE_RULE_STATE_PROPOSED
        || tradeRuleState === TRADE_RULE_STATE_ACCEPTED) {
        buttons = intl.formatMessage({ id: 'trade_rule.trade_rule_timeline.no_actions.label', defaultMessage: "'No actions available'" });
      }
    }

    return tradeRuleActions(buttons);
  };

  // Build a map of tradePointId => Meter for display in the trade rule timeline event
  const { meters, timezone } = propertyData;
  const metersEdges = meters?.edges ?? [];
  const metersMap = {};
  metersEdges.forEach(({ node }) => {
    metersMap[node.tradePointId] = node;
  });

  // Extract the trade rule information required for each event
  const {
    uuid, tradeType, clauses, buyer, seller, proposedAt, proposedBy, acceptedAt, acceptedBy,
    rejectedAt, rejectedBy, cancelledAt, cancelledBy, closedAt, closedBy,
  } = ruleData;

  const tradeRuleState = getTradeRuleState(ruleData);

  const buttons = tradeRuleActionButton(ruleData, metersMap, tradeRuleState);

  return (
    <ol className="m-0 p-0">
      <TradeRuleTimelineStepProposed
        buyer={buyer}
        clauses={clauses}
        hasNextStep={tradeRuleState !== TRADE_RULE_STATE_PROPOSED || !!buttons}
        isCurrentStep={tradeRuleState === TRADE_RULE_STATE_PROPOSED}
        ruleId={uuid}
        seller={seller}
        timestamp={proposedAt}
        timezone={timezone}
        tradeType={tradeType}
        user={proposedBy}
      />
      {acceptedAt && (
        <TradeRuleTimelineStepAccepted
          buyer={buyer}
          clauses={clauses}
          hasNextStep={tradeRuleState !== TRADE_RULE_STATE_ACCEPTED || !!buttons}
          isCurrentStep={tradeRuleState === TRADE_RULE_STATE_ACCEPTED}
          metersMap={metersMap}
          propertyTimeZone={timezone}
          ruleId={uuid}
          seller={seller}
          timestamp={acceptedAt}
          tradeType={tradeType}
          user={acceptedBy}
        />
      )}
      {closedAt && (
        <TradeRuleTimelineStepClosed
          buyer={buyer}
          isCurrentStep={tradeRuleState === TRADE_RULE_STATE_CLOSED}
          hasNextStep={tradeRuleState !== TRADE_RULE_STATE_CLOSED || !!buttons}
          metersMap={metersMap}
          propertyTimeZone={timezone}
          ruleId={uuid}
          seller={seller}
          timestamp={closedAt}
          tradeType={tradeType}
          user={closedBy}
        />
      )}
      {cancelledAt && (
        <TradeRuleTimelineStepCancelled
          isCurrentStep={tradeRuleState === TRADE_RULE_STATE_CANCELLED}
          hasNextStep={tradeRuleState !== TRADE_RULE_STATE_CANCELLED || !!buttons}
          propertyTimeZone={timezone}
          ruleId={uuid}
          timestamp={cancelledAt}
          tradeType={tradeType}
          user={cancelledBy}
        />
      )}
      {rejectedAt && (
        <TradeRuleTimelineStepRejected
          isCurrentStep={tradeRuleState === TRADE_RULE_STATE_REJECTED}
          hasNextStep={tradeRuleState !== TRADE_RULE_STATE_REJECTED || !!buttons}
          timezone={timezone}
          ruleId={uuid}
          tradeType={tradeType}
          timestamp={rejectedAt}
          user={rejectedBy}
        />
      )}
      {buttons}
    </ol>
  );
}

TradeRuleTimeline.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  rule: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default TradeRuleTimeline;
